import { useState } from 'react';
import { useReveal } from '@/hooks/useReveal';

const courses = [
  'Information Security',
  'Network Defense',
  'Incident Response',
  'Digital Forensics',
  'Penetration Testing',
  'Industrial Control System Security',
  'Cloud Security',
];

const courseGroups = [
  {
    level: 'Lower Division Undergraduate',
    courses: [
      {
        code: 'ITSY 1342',
        title: 'Information Technology Security',
        description: 'This foundational course introduces students to the core principles of information assurance, including the CIA triad (Confidentiality, Integrity, Availability), threat modeling, access control frameworks, and the evolving cyber threat landscape. Students gain hands-on exposure to vulnerability assessment, risk mitigation strategies, and the regulatory environment shaping modern security operations. The course builds a strong conceptual baseline that prepares students for advanced coursework and for entry-level roles in a Security Operations Center (SOC).',
      },
      {
        code: 'ITSE 1302',
        title: 'Computer Programming',
        description: 'Students develop fundamental programming proficiency in a structured language, mastering control flow, data structures, functions, file I/O, and object-oriented design principles. The course emphasizes secure coding practices, debugging methodologies, and algorithmic problem-solving as essential skills for cybersecurity professionals who must read, audit, and write code in operational environments. Coursework integrates scripting exercises directly applicable to automation tasks performed in a Security Operations Center (SOC), including log parsing and alert triage scripting.',
      },
      {
        code: 'ITSY 2300',
        title: 'Operating System Security',
        description: 'This course provides an in-depth examination of operating system internals across Windows, Linux, and Unix platforms, focusing on hardening techniques, privilege escalation vectors, kernel-level security mechanisms, and secure configuration baselines. Students perform hands-on labs involving patch management, audit logging, file system permissions, and host-based intrusion detection. Graduates leave equipped to support endpoint defense functions within a Security Operations Center (SOC), where OS-level telemetry forms the backbone of threat detection.',
      },
      {
        code: 'ITSY 2301',
        title: 'Firewalls and Network Security',
        description: 'Students explore the architecture, deployment, and management of enterprise firewall systems, intrusion detection and prevention platforms, VPNs, and next-generation perimeter defenses. The curriculum emphasizes packet-level analysis, network segmentation strategies, defense-in-depth design, and secure protocol implementation across layered network environments. Hands-on labs simulate Security Operations Center (SOC) workflows where analysts monitor, tune, and respond to alerts generated by network defense appliances.',
      },
    ],
  },
  {
    level: 'Upper Division Undergraduate',
    courses: [
      {
        code: 'ITSY 2341',
        title: 'Security Management Practices',
        description: 'This course covers the strategic and administrative dimensions of cybersecurity, including governance frameworks (NIST CSF, ISO 27001, COBIT), policy development, security awareness program design, and the alignment of security objectives with broader organizational mission. Students examine personnel security, business continuity planning, and the management of security operations at an enterprise scale. The course prepares students to serve as Security Operations Center (SOC) team leads, GRC analysts, or cybersecurity program managers.',
      },
      {
        code: 'ITSY 2342',
        title: 'Incident Response and Handling',
        description: 'Students master the full incident response lifecycle—preparation, identification, containment, eradication, recovery, and lessons learned—aligned with NIST SP 800-61 and SANS PICERL methodologies. Coursework includes hands-on triage of simulated breaches, evidence preservation, chain-of-custody procedures, and the development of incident response playbooks. The class is structured to mirror real-world Security Operations Center (SOC) escalation procedures, equipping students to function as Tier 1 and Tier 2 incident responders from day one.',
      },
      {
        code: 'ITSY 2343',
        title: 'Computer Systems Forensics',
        description: "This course delivers rigorous training in the acquisition, preservation, and analysis of digital evidence from computer systems, file systems, and volatile memory. Students use industry-standard tools such as Autopsy, FTK, and Volatility to recover artifacts, reconstruct user activity, and produce court-admissible forensic reports. The curriculum emphasizes the forensic analyst's role within a Security Operations Center (SOC), where post-incident investigations directly inform threat intelligence and detection engineering.",
      },
      {
        code: 'ITSY 2345',
        title: 'Network Defense and Countermeasures',
        description: 'Students develop advanced skills in defending enterprise networks against sophisticated adversaries, with emphasis on detection engineering, network traffic analysis, threat hunting, and active defense tactics. The course covers SIEM platform operation, Snort/Suricata rule writing, NetFlow analysis, and the deployment of deception technologies such as honeypots. Lab environments replicate Security Operations Center (SOC) tooling, giving students realistic experience in alert triage, threat correlation, and adversary tracking.',
      },
    ],
  },
  {
    level: 'Advanced Undergraduate',
    courses: [
      { code: 'CYBR 3320', title: 'Digital and Device Forensics', description: 'This advanced forensics course extends beyond traditional computer systems to address mobile device, IoT, vehicle, and embedded system forensics, reflecting the explosion of evidence sources in modern investigations. Students perform chip-off extractions, mobile artifact analysis, cloud-based evidence collection, and forensic imaging across heterogeneous device ecosystems. The course prepares graduates for roles in digital forensics units, e-discovery firms, and Security Operations Center (SOC) forensic teams supporting enterprise investigations.' },
      { code: 'CSIS 3313', title: 'Information Security Standards, Risk Management, and Compliance', description: 'Students study the major regulatory and compliance frameworks shaping modern cybersecurity—HIPAA, PCI-DSS, SOX, GDPR, FISMA, NIST 800-53, and ISO 27001—alongside formal risk management methodologies including FAIR and OCTAVE. Coursework includes risk register development, control mapping, audit preparation, and the construction of compliance documentation suitable for regulatory submission. The course produces graduates equipped to support GRC functions and to translate Security Operations Center (SOC) findings into compliance-relevant evidence.' },
      { code: 'CSIS 3353', title: 'Cyber Law and the Legal System', description: 'This course examines the intersection of law, policy, and cybersecurity, covering the Computer Fraud and Abuse Act (CFAA), Electronic Communications Privacy Act (ECPA), digital evidence admissibility standards, intellectual property protections, and emerging case law in cyberspace. Students analyze landmark cybercrime prosecutions, privacy litigation, and international jurisdictional disputes that shape investigative practice. The curriculum directly informs how Security Operations Center (SOC) analysts and incident responders must document, preserve, and disclose evidence to remain legally defensible.' },
      { code: 'CYBR 3340', title: 'Cyber Crime', description: 'Students investigate the methods, motivations, and ecosystems of modern cybercriminals, including ransomware operators, nation-state actors, financial fraud rings, and dark web marketplaces. The course examines real-world case studies of major breaches, cryptocurrency-enabled crime, social engineering campaigns, and the global law enforcement response. Coursework prepares students to contribute to threat intelligence functions within a Security Operations Center (SOC), translating adversary behavior into actionable detection and defense strategies.' },
      { code: 'CYBR 3371', title: 'Industrial Control System Security', description: 'This specialized course addresses the unique security challenges of operational technology (OT) environments, including SCADA systems, PLCs, distributed control systems, and the converging IT/OT landscape across critical infrastructure sectors. Students examine landmark ICS attacks (Stuxnet, Industroyer, TRITON), the Purdue Model architecture, and the ICS-specific frameworks NIST 800-82 and IEC 62443. The course prepares students for OT-focused roles in energy, manufacturing, and utilities, including specialized Security Operations Center (SOC) positions monitoring industrial networks.' },
    ],
  },
  {
    level: 'Senior Level',
    courses: [
      { code: 'ITCS 4315', title: 'Cybersecurity Incident Response Team', description: 'This capstone-level course immerses students in the operational realities of a Computer Security Incident Response Team (CSIRT), covering team structure, communication protocols, escalation procedures, and coordination with legal, executive, and external stakeholders during active incidents. Students execute full-scale tabletop exercises and live-fire simulations that replicate ransomware outbreaks, insider threat events, and supply chain compromises. The course is structured to mirror enterprise Security Operations Center (SOC) and CSIRT integration, preparing graduates for leadership roles in incident command.' },
      { code: 'CYBR 4310', title: 'Penetration Testing', description: 'Students master offensive security methodologies aligned with the PTES, OSSTMM, and MITRE ATT&CK frameworks, conducting full-cycle penetration tests against networks, web applications, wireless infrastructure, and Active Directory environments. Hands-on labs leverage Kali Linux, Metasploit, Burp Suite, and custom exploitation tooling to identify, exploit, and document vulnerabilities in realistic enterprise targets. The course produces practitioners capable of supporting red team engagements and feeding findings directly into Security Operations Center (SOC) detection engineering pipelines.' },
      { code: 'CSIS 4323', title: 'IT Security Auditing', description: 'This course trains students in the systematic evaluation of information system controls, audit planning, evidence gathering, and the production of formal audit reports aligned with ISACA standards and the COBIT framework. Coursework covers technical control testing, application-level audits, third-party risk assessments, and the preparation of materials for SOC 2, ISO 27001, and PCI-DSS engagements. Graduates emerge prepared to serve as IT auditors, GRC analysts, or compliance liaisons supporting Security Operations Center (SOC) reporting and continuous control monitoring.' },
      { code: 'CYBR 4320', title: 'Cyber Defense Operations', description: 'Students operate as analysts inside a fully simulated Security Operations Center (SOC), executing real-time threat detection, alert triage, threat hunting, and coordinated incident response against live adversary emulation. The curriculum integrates SIEM platforms, EDR tooling, threat intelligence feeds, and SOAR automation to replicate the tempo and complexity of enterprise defensive operations. The course produces graduates ready to step directly into Tier 2 and Tier 3 SOC analyst roles with operational fluency rare among new hires.' },
      { code: 'CYBR 4330', title: 'Virtualization and Cloud Security', description: 'This course addresses the security architecture of virtualized and cloud-native environments across AWS, Azure, and Google Cloud, including hypervisor security, container hardening (Docker, Kubernetes), serverless threat models, and Cloud Security Posture Management (CSPM). Students perform hands-on labs in IAM hardening, cloud-native logging, infrastructure-as-code security review, and the detection of cloud-specific attack patterns such as IMDS abuse and OAuth token theft. The curriculum equips graduates to support cloud-focused Security Operations Center (SOC) functions, where visibility and detection in elastic, ephemeral environments are mission-critical.' },
    ],
  },
];

type Course = { code: string; title: string; description: string | null };

const roles = [
  {
    icon: 'ri-graduation-cap-line',
    title: 'Full-Time Cybersecurity Faculty',
    desc: 'Teaching the full spectrum of cybersecurity disciplines at San Jacinto College since January 2022.',
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Cybersecurity Club Advisor',
    desc: 'Mentoring the next generation of defenders outside the classroom — building community, competition readiness, and career awareness.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'SkillsUSA Cybersecurity Advisor',
    desc: 'Preparing students for national-level competition in cybersecurity skills and professional development.',
  },
  {
    icon: 'ri-government-line',
    title: 'CAE Cybersecurity Club Committee Member',
    desc: 'Contributing to the National Centers of Academic Excellence in Cybersecurity program at the institutional level.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'Grant Writer',
    desc: 'Authored the NSF grant proposal that secured ~$300,000 and earned San Jacinto College its designation as a National Center of Academic Excellence in Cybersecurity (CAE-C).',
  },
];

export default function Teaching() {
  const headRef = useReveal(0.1);
  const rolesRef = useReveal(0.08);
  const coursesRef = useReveal(0.1);
  const grantRef = useReveal(0.1);
  const [showCourses, setShowCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  return (
    <section id="teaching" className="bg-[#F5F1E8] py-28 md:py-40 overflow-hidden">
      <div className="px-6 md:px-12 lg:px-20 max-w-screen-xl mx-auto">

        {/* Header */}
        <div
          ref={headRef}
          className="opacity-0 translate-y-8 transition-all duration-1000 ease-out mb-20 md:mb-24"
        >
          <p
            className="text-[#C9A84C] text-xs tracking-[0.35em] uppercase mb-6 italic"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '13px' }}
          >
            Faculty &amp; Education
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2
              className="text-[#0A0A0A] text-5xl md:text-7xl font-light leading-[1.1]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Inside the
              <br />
              <span className="italic">Classroom</span>
            </h2>
            <div className="max-w-sm space-y-5">
              <p
                className="text-[#5C5752] text-sm font-light leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Full-Time Cybersecurity Faculty at{' '}
                <a
                  href="https://www.sanjac.edu"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[#C9A84C] hover:underline cursor-pointer"
                >
                  San Jacinto College
                </a>
                {' '}in Houston, Texas. Bringing federal-level operational knowledge directly into the curriculum since January 2022.
              </p>
              <blockquote className="border-l-2 border-[#C9A84C] pl-5">
                <p
                  className="text-[#0A0A0A] text-base font-light italic leading-relaxed"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px' }}
                >
                  &ldquo;Firewalls block the knock. Threat intelligence tells you who&apos;s been casing the building.&rdquo;
                </p>
                <p
                  className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase mt-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  — Professor Lozano
                </p>
              </blockquote>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 mb-20">

          {/* Roles — left 3 cols */}
          <div
            ref={rolesRef}
            className="lg:col-span-3 opacity-0 translate-y-8 transition-all duration-1000 ease-out"
          >
            <p
              className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Roles &amp; Responsibilities
            </p>
            <div className="space-y-6">
              {roles.map((role) => (
                <div
                  key={role.title}
                  className="flex items-start gap-5 p-6 rounded-2xl bg-[#0A0A0A]/4 hover:bg-[#0A0A0A]/6 transition-colors duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`${role.icon} text-[#C9A84C] text-xl`}></i>
                  </div>
                  <div>
                    <h4
                      className="text-[#0A0A0A] text-lg font-light leading-tight mb-2"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {role.title}
                    </h4>
                    <p
                      className="text-[#5C5752] text-sm leading-relaxed"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {role.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Courses + image — right 2 cols */}
          <div
            ref={coursesRef}
            className="lg:col-span-2 opacity-0 translate-y-8 transition-all duration-1000 ease-out space-y-8"
          >
            {/* Classroom image — San Jac */}
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '240px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20college%20cybersecurity%20classroom%20lab%20with%20students%20at%20computers%2C%20professional%20academic%20environment%2C%20warm%20editorial%20lighting%2C%20focused%20students%20learning%20technology%2C%20San%20Jacinto%20College%20style%20setting%2C%20Houston%20Texas%2C%20documentary%20photography&width=500&height=240&seq=teach_sjc_img01&orientation=landscape"
                alt="San Jacinto College cybersecurity classroom"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <p className="text-[#F5F1E8] text-xs tracking-[0.2em] uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                  San Jacinto College
                </p>
                <p className="text-[#C9A84C] text-[10px] tracking-[0.15em] uppercase mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Houston, Texas
                </p>
              </div>
            </div>

            {/* Courses taught — clickable */}
            <div className="bg-[#0A0A0A] rounded-2xl p-7">
              <div className="flex items-center justify-between mb-5">
                <p
                  className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Courses Taught
                </p>
                <span className="text-[#8B8680] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>19 courses</span>
              </div>
              <ul className="space-y-3 mb-6">
                {courses.map((course) => (
                  <li key={course} className="flex items-center gap-3">
                    <span className="text-[#C9A84C] text-xs shrink-0">—</span>
                    <span className="text-[#F5F1E8]/80 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {course}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setShowCourses(true)}
                className="whitespace-nowrap w-full flex items-center justify-center gap-2 border border-[#C9A84C]/30 text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase rounded-full py-3 hover:bg-[#C9A84C]/10 transition-all duration-300 cursor-pointer"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                View All 19 Courses
                <i className="ri-arrow-right-line text-xs"></i>
              </button>
            </div>
          </div>
        </div>

        {/* SOC Lys — full width, above grant cards */}
        <div className="mb-16 rounded-3xl overflow-hidden" style={{ height: '680px' }}>
          <img
            src="https://raw.githubusercontent.com/AlyssaLozano/LozanoCollective/main/public/images/SOC%20Lys.jpg"
            alt="Alyssa Lozano"
            className="w-full h-full object-cover object-top"
            referrerPolicy="no-referrer"
          />
        </div>

        {/* Grant & Reviewer Cards */}
        <div
          ref={grantRef}
          className="opacity-0 translate-y-8 transition-all duration-1000 ease-out grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {/* NSA Teaching — 500+ */}
          <div className="border border-[#C9A84C]/20 rounded-3xl p-8 md:p-10 flex flex-col gap-6 bg-[#0A0A0A]/3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20">
              <i className="ri-shield-star-line text-[#C9A84C] text-2xl"></i>
            </div>
            <div>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                National Security Agency
              </p>
              <h3
                className="text-[#0A0A0A] text-2xl md:text-3xl font-light leading-[1.2] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Teaching at
                <br />
                <span className="italic">the Source</span>
              </h3>
              <p
                className="text-[#5C5752] text-sm leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Taught over 500 active duty military personnel and civilians in cyber threat intelligence and cybersecurity defense while serving at the NSA — bringing operational knowledge directly to the people on the front lines.
              </p>
            </div>
            <div>
              <p
                className="text-[#0A0A0A] text-4xl font-light"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                500+
              </p>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Military &amp; Civilians Trained
              </p>
            </div>
          </div>

          {/* NSF Grant — San Jac */}
          <div className="border border-[#C9A84C]/20 rounded-3xl p-8 md:p-10 flex flex-col gap-6 bg-[#0A0A0A]/3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20">
              <i className="ri-file-text-line text-[#C9A84C] text-2xl"></i>
            </div>
            <div>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                San Jacinto College
              </p>
              <h3
                className="text-[#0A0A0A] text-2xl md:text-3xl font-light leading-[1.2] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                NSF Principal Investigator
                <br />
                <span className="italic">&amp; Grant Writer</span>
              </h3>
              <p
                className="text-[#5C5752] text-sm leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Authored and led the NSF grant proposal that secured ~$300,000 and earned San Jacinto College its designation as a National Center of Academic Excellence in Cybersecurity (CAE-C), one of the most significant recognitions in cybersecurity education.
              </p>
            </div>
            <div>
              <p
                className="text-[#0A0A0A] text-4xl font-light"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                ~$300K
              </p>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                NSF Research Grant
              </p>
            </div>
          </div>

          {/* NSF Reviewer — Independent Contract */}
          <div className="border border-[#C9A84C]/20 rounded-3xl p-8 md:p-10 flex flex-col gap-6 bg-[#0A0A0A]/3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20">
              <i className="ri-search-eye-line text-[#C9A84C] text-2xl"></i>
            </div>
            <div>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Independent Contract
              </p>
              <h3
                className="text-[#0A0A0A] text-2xl md:text-3xl font-light leading-[1.2] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                NSF Grant
                <br />
                <span className="italic">Reviewer</span>
              </h3>
              <p
                className="text-[#5C5752] text-sm leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Selected on a contract basis to review grant proposals for the NSF Advanced Technological Education (ATE) program, evaluating submissions from institutions nationwide for scientific merit and educational impact.
              </p>
            </div>
            <div>
              <p
                className="text-[#0A0A0A] text-lg font-light"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                NSF Advanced Technological Education
              </p>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Contract Reviewer
              </p>
            </div>
          </div>

          {/* Student Recognition — ATE Conference */}
          <div className="border border-[#C9A84C]/20 rounded-3xl p-8 md:p-10 flex flex-col gap-6 bg-[#0A0A0A]/3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20">
              <i className="ri-award-line text-[#C9A84C] text-2xl"></i>
            </div>
            <div>
              <p
                className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Student Success
              </p>
              <h3
                className="text-[#0A0A0A] text-2xl md:text-3xl font-light leading-[1.2] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                ATE Conference
                <br />
                <span className="italic">Recognition</span>
              </h3>
              <p
                className="text-[#5C5752] text-sm leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                San Jacinto College cybersecurity students recognized at the 2025 ATE Conference for their research and technical excellence, a direct reflection of the program&apos;s rigor and the faculty behind it.
              </p>
            </div>
            <a
              href="https://www.sanjac.edu/about/news/2025/san-jac-cybersecurity-students-recognized-at-ate-conference"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="whitespace-nowrap inline-flex items-center gap-2 text-[#C9A84C] text-xs tracking-[0.2em] uppercase font-medium hover:gap-3 transition-all cursor-pointer"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Read the Story
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>

      </div>

      {/* Courses Modal */}
      {showCourses && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
          style={{ backgroundColor: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowCourses(false); setSelectedCourse(null); } }}
        >
          <div className="bg-[#F5F1E8] rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="sticky top-0 bg-[#0A0A0A] rounded-t-3xl px-8 py-6 flex items-start justify-between gap-4 z-10">
              <div>
                {selectedCourse ? (
                  <>
                    <button
                      onClick={() => setSelectedCourse(null)}
                      className="whitespace-nowrap inline-flex items-center gap-2 text-[#8B8680] hover:text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase mb-3 transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-xs"></i>
                      All Courses
                    </button>
                    <p className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {selectedCourse.code}
                    </p>
                    <h3 className="text-[#F5F1E8] text-2xl md:text-3xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {selectedCourse.title}
                    </h3>
                  </>
                ) : (
                  <>
                    <p className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Teaching Experience
                    </p>
                    <h3 className="text-[#F5F1E8] text-2xl md:text-3xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Courses Taught
                    </h3>
                    <p className="text-[#8B8680] text-xs mt-2 leading-relaxed max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Courses span introductory through senior-level cybersecurity, emphasizing progression from systems fundamentals to applied cyber defense and industrial control system security. Click any course to view the full description.
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={() => { setShowCourses(false); setSelectedCourse(null); }}
                className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-[#8B8680] hover:text-[#F5F1E8] hover:border-white/40 transition-all cursor-pointer shrink-0 mt-1"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Modal body — course detail */}
            {selectedCourse ? (
              <div className="px-8 py-8">
                <div className="bg-white rounded-2xl p-7 border border-[#0A0A0A]/6">
                  <p className="text-[#3A3632] text-sm leading-[1.9]">{selectedCourse.description}</p>
                </div>
              </div>
            ) : (
              /* Modal body — course list */
              <div className="px-8 py-8 space-y-8">
                {courseGroups.map((group, gi) => (
                  <div key={group.level}>
                    <div className="flex items-center gap-4 mb-4">
                      <p
                        className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase font-medium shrink-0"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {group.level}
                      </p>
                      <div className="flex-1 h-px bg-[#0A0A0A]/10"></div>
                      <span className="text-[#8B8680] text-[10px] shrink-0" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {group.courses.length} courses
                      </span>
                    </div>
                    <div className="space-y-2">
                      {group.courses.map((c) => (
                        <div
                          key={c.code}
                          onClick={() => c.description && setSelectedCourse(c)}
                          className={`flex items-center gap-4 p-4 rounded-xl bg-[#0A0A0A]/4 transition-colors duration-200 ${c.description ? 'hover:bg-[#0A0A0A]/8 cursor-pointer group' : ''}`}
                        >
                          <span
                            className="text-[#C9A84C] text-xs font-medium tracking-wide shrink-0 w-24"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {c.code}
                          </span>
                          <span
                            className="text-[#0A0A0A] text-sm leading-relaxed flex-1"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {c.title}
                          </span>
                          {c.description && (
                            <i className="ri-arrow-right-line text-[#C9A84C] text-sm shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                          )}
                        </div>
                      ))}
                    </div>
                    {gi < courseGroups.length - 1 && <div className="mt-8 h-px bg-[#0A0A0A]/8"></div>}
                  </div>
                ))}

                {/* Total count */}
                <div className="border-t border-[#0A0A0A]/10 pt-6 flex items-center justify-between">
                  <p className="text-[#5C5752] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                    San Jacinto College &mdash; Houston, Texas
                  </p>
                  <div className="text-right">
                    <p className="text-[#0A0A0A] text-3xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>19</p>
                    <p className="text-[#C9A84C] text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Total Courses</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </section>
  );
}
